import { StateStorage, createJSONStorage, PersistOptions } from 'zustand/middleware'
import { storage } from './storage'
import { WidgetSettings } from '@/types'
import type { MenuType } from '@/types/widget'
import { CONTACT_ID_KEY } from './contactIdentity'

const STORAGE_PREFIX = 'widget-ui:'

function isDemoMode(): boolean {
  return new URLSearchParams(window.location.search).get('demo') === 'true'
}

type StorageType = 'local' | 'session'

const localStorageAdapter: StateStorage = {
  getItem: (name) => {
    if (isDemoMode()) return null
    return storage.get<string>(`${STORAGE_PREFIX}${name}`) ?? null
  },
  setItem: (name, value) => {
    if (isDemoMode()) return
    storage.set(`${STORAGE_PREFIX}${name}`, value)
  },
  removeItem: (name) => storage.remove(`${STORAGE_PREFIX}${name}`),
}

const sessionStorageAdapter: StateStorage = {
  getItem: (name) => {
    if (isDemoMode()) return null
    return sessionStorage.getItem(`${STORAGE_PREFIX}${name}`)
  },
  setItem: (name, value) => {
    if (isDemoMode()) return
    sessionStorage.setItem(`${STORAGE_PREFIX}${name}`, value)
  },
  removeItem: (name) => sessionStorage.removeItem(`${STORAGE_PREFIX}${name}`),
}

type PersistConfig<T, Keys extends keyof T> = {
  name: string
  pick: Keys[]
  version?: number
  storageType?: StorageType
  onHydrate?: (state: T | undefined) => void
  /**
   * Optional final transform on the picked slice before it hits storage.
   * Use to strip transient values from a picked field (e.g. drop in-flight
   * placeholders from a persisted messages array).
   */
  transform?: (partial: Pick<T, Keys>) => Pick<T, Keys>
}

export function createPersistOptions<T, Keys extends keyof T>(
  config: PersistConfig<T, Keys>
): PersistOptions<T, Pick<T, Keys>> {
  const storageAdapter = config.storageType === 'session' 
    ? sessionStorageAdapter 
    : localStorageAdapter

  return {
    name: config.name,
    version: config.version ?? 1,
    storage: createJSONStorage(() => storageAdapter),
    partialize: (state) => {
      const partial = {} as Pick<T, Keys>
      for (const key of config.pick) {
        partial[key] = state[key]
      }
      return config.transform ? config.transform(partial) : partial
    },
    onRehydrateStorage: config.onHydrate ? () => config.onHydrate : undefined,
  }
}

const SESSION_CONTEXT_KEY = 'widget-ui:session-context'

type SessionContext = {
  uids: string[]
  configGlobalID: string | undefined
}

export const sessionContext = {
  get: (): SessionContext | undefined =>
    storage.get<SessionContext>(SESSION_CONTEXT_KEY),

  set: (ctx: SessionContext) =>
    storage.set(SESSION_CONTEXT_KEY, ctx),
}

const WIDGET_SETTINGS_TTL = 60_000 // 1 minute

type CachedSettings = {
  data: WidgetSettings
  uid: string
  globalID: string | undefined
  menuType: MenuType | undefined
  cachedAt: number
}

let settingsCache: CachedSettings | null = null

export const widgetSettingsCache = {
  get: (
    isDemo: boolean,
    uid: string,
    globalID: string | undefined,
    menuType: MenuType | undefined
  ): WidgetSettings | null => {
    if (isDemo || !settingsCache) return null
    if (Date.now() - settingsCache.cachedAt > WIDGET_SETTINGS_TTL) {
      settingsCache = null
      return null
    }
    if (
      settingsCache.uid !== uid ||
      settingsCache.globalID !== globalID ||
      settingsCache.menuType !== menuType
    ) {
      return null
    }
    return settingsCache.data
  },

  set: (
    settings: WidgetSettings,
    uid: string,
    globalID: string | undefined,
    menuType: MenuType | undefined,
    isDemo: boolean
  ) => {
    if (isDemo) return
    settingsCache = { data: settings, uid, globalID, menuType, cachedAt: Date.now() }
  },

  clear: () => {
    settingsCache = null
  },
}

export function clearAllPersistedData(): void {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key)
  })

  // Shared flat key used by pixel + widget — outside the widget-ui: prefix.
  localStorage.removeItem(CONTACT_ID_KEY)

  const sessionKeysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key?.startsWith(STORAGE_PREFIX)) {
      sessionKeysToRemove.push(key)
    }
  }
  sessionKeysToRemove.forEach((key) => {
    sessionStorage.removeItem(key)
  })

  widgetSettingsCache.clear()
}
